import type {GetStaticProps} from 'next';
import {useTranslation} from '@app/hooks/use-translation';
import {serverSideTranslations} from '@app/modules/locale-management/server';
import {LandingSections} from '@tempo/landing';
import {
	Hero,
	Song,
	Live,
	Trivia,
	Cheating,
	FAQ,
} from '@app/views/features/music-quiz';
import {TempoLandingPage} from '@app/components/layout/LandingPage';
import type {LandingProps} from '@app/types/next';
import {PlaylistSection} from '@app/views/generic';
import {useMusicQuizPageData} from '@app/modules/ssg-data/features/music-quiz/client';
import {getMusicQuizPageGQLData} from '@app/modules/ssg-data/features/music-quiz/server';
import {getFooterLinksPerLang} from '@app/modules/ssg-data/unlogged-data';
import type {MusicQuizPageQuery} from '@app/modules/graphql/generated';

type Playlists = Exclude<keyof MusicQuizPageQuery, '__typename'>;

const MusicQuizPage: React.FC<LandingProps> = () => {
	const {t} = useTranslation({ns: ['musicquiz']});
	const pageTitle = t('musicquizlandingpage_text_seotitle_standalone-web');
	const pageDescription = t(
		'musicquizlandingpage_text_seodescription_standalone-web',
	);
	const {data} = useMusicQuizPageData();

	function formatGames(data: MusicQuizPageQuery[Playlists]) {
		return {
			trad: data?.title ?? '',
			link: 'https://link.deezer.com/?dest=https%3A%2F%2Fwww.deezer.com%2Fchannels%2Fgames%3Fbypassreferer%3D1&amv=8.0.12&imv=10.12.0',
			src: data?.cover.urls[0] ?? '',
			label: `games-${data?.title}`,
		};
	}

	const playlists = data
		? [
				formatGames(data.rap90),
				formatGames(data.disney),
				formatGames(data.moviesTV),
				formatGames(data.tiktokhits),
				formatGames(data.annee80),
			]
		: [];

	return (
		<TempoLandingPage title={pageTitle} description={pageDescription}>
			<LandingSections topGutter>
				<Hero />
				<PlaylistSection
					title={t(
						'musicquizlandingpage_title_fingersonbuzzers_standalone-web',
					)}
					description={t(
						'musicquizlandingpage_text_fingersonbuzzers_standalone-web',
					)}
					playlistItems={playlists}
				/>
				<Song />
				<Live />
				<Trivia />
				<Cheating />
				<FAQ />
			</LandingSections>
		</TempoLandingPage>
	);
};

export const getStaticProps: GetStaticProps<LandingProps> = async ({
	locale = '',
	locales = [],
}) => {
	const translations = await serverSideTranslations(locale, [
		'musicquiz',
		'common',
	]);
	return {
		props: {
			...translations,
			dehydratedState: {
				...(await getFooterLinksPerLang({locale, locales})),
				...(await getMusicQuizPageGQLData({locale})),
			},
			tracking: {
				pagecategory: 'features',
				pagename: 'music-quiz',
				cdpViewId: 'features_music_quiz',
			},
		},
		revalidate: 15 * 60,
	};
};

export default MusicQuizPage;
